import { useMutation, UseMutationResult } from '@tanstack/react-query'
import { useApiWithIDToken } from '../../state/modules/common'
import { useNotifications } from '../../state/modules/notifications'
import { Variant } from '../../common/types'
import {
  extractDocumentFiles,
  PreparedFile,
} from '../../components/helpers/DocumentExchange/extractDocumentFiles'

/**
 * Fetch the most recently uploaded file(s) for a given checklist document
 * and prepare them for previewing/downloading.
 *
 * @returns A UseMutationResult object for triggering the fetch, resolving
 * with one prepared file per uploaded file.
 */
export const usePreviewChecklistDocument = (): UseMutationResult<
  PreparedFile[],
  Error,
  { documentId: number; documentName: string }
> => {
  const api = useApiWithIDToken()
  const { send: sendNotification } = useNotifications()

  return useMutation({
    mutationFn: ({ documentId, documentName }) =>
      previewChecklistDocument(api, documentId, documentName),
    onError: (error, vars) => {
      // The user-facing message stays simple and free of internal detail
      // (e.g. zip-parsing specifics) that isn't actionable for the user, so
      // log the full error for developers/support instead.
      console.error(error)
      sendNotification({
        title: 'Preview Failed',
        message: `An error occurred while trying to open ${vars.documentName}. Please try again.`,
        variant: Variant.DANGER,
        useRucksackUI: true,
      })
    },
  })
}

async function previewChecklistDocument(
  api: ReturnType<typeof useApiWithIDToken>,
  documentId: number,
  documentName: string
): Promise<PreparedFile[]> {
  try {
    const url = `/documents/${documentId}/preview`
    const response = await api.get<string>(url, {
      responseType: 'blob',
    })
    return await extractDocumentFiles(response.data)
  } catch (error) {
    throw new Error(
      `Failed to fetch preview for document ${documentName}: ${
        error instanceof Error ? error.message : 'Unknown error'
      }`,
      { cause: error }
    )
  }
}
